import React, { useMemo } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import PropTypes from 'prop-types';
import { Card } from 'react-bootstrap';
import {
    LineChart
} from 'echarts/charts';
import {
    GridComponent,
    TooltipComponent,
    LegendComponent
} from 'echarts/components';
import {
    CanvasRenderer
} from 'echarts/renderers';
import { ChartSkeleton } from './SkeletonLoader';

echarts.use([LineChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

const AkasiaPosActivityChart = ({ activityData, title, onDateClick, filterElement, loading }) => {
    if (loading) {
        return <ChartSkeleton title={title} />;
    }

    const sortedData = useMemo(() => {
        if (!activityData || activityData.length === 0) return [];
        return activityData;
    }, [activityData]);

    const option = useMemo(() => {
        if (!sortedData.length) return {};

        const dates = sortedData.map(item => {
            let dStr = item.dFecha;

            if (typeof dStr === 'string' && dStr.includes('T')) {
                dStr = dStr.split('T')[0];
            }

            if (dStr.includes('/') && dStr.includes(':')) return dStr;

            if (dStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
                const parts = dStr.split('-');
                const dateObj = new Date(parts[0], parts[1] - 1, parts[2]);
                return dateObj.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
            }

            return dStr;
        });

        const counts = sortedData.map(item => item.iLicenciasConActividad || 0);

        return {
            tooltip: {
                trigger: 'axis'
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: dates,
                axisLabel: {
                    rotate: 45
                }
            },
            yAxis: {
                type: 'value',
                name: 'Licencias',
                minInterval: 1
            },
            series: [
                {
                    name: 'Actividad',
                    type: 'line',
                    data: counts,
                    smooth: true,
                    itemStyle: {
                        color: '#0d6efd'
                    },
                    areaStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0, color: 'rgba(13, 110, 253, 0.3)' },
                            { offset: 1, color: 'rgba(13, 110, 253, 0.05)' }
                        ])
                    }
                }
            ]
        };
    }, [sortedData]);

    const onEvents = useMemo(() => {
        if (!onDateClick) return {};
        return {
            click: (params) => {
                if (params.componentType === 'series') {
                    const clickedDate = sortedData[params.dataIndex]?.dFecha;
                    if (clickedDate) onDateClick(clickedDate);
                }
            }
        };
    }, [onDateClick, sortedData]);

    if (!activityData || activityData.length === 0) {
        return (
            <Card className="h-100 shadow-sm">
                <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">{title}</h6>
                    {filterElement && filterElement}
                </Card.Header>
                <Card.Body className="d-flex align-items-center justify-content-center">
                    <p className="text-muted">No hay datos de actividad disponibles.</p>
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card className="h-100 shadow-sm">
            <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                <h6 className="mb-0">{title}</h6>
                {filterElement && filterElement}
            </Card.Header>
            <Card.Body>
                <ReactEChartsCore
                    echarts={echarts}
                    option={option}
                    onEvents={onEvents}
                    style={{ height: '300px', width: '100%' }}
                />
            </Card.Body>
        </Card>
    );
};

AkasiaPosActivityChart.propTypes = {
    activityData: PropTypes.array,
    title: PropTypes.string,
    onDateClick: PropTypes.func,
    filterElement: PropTypes.node,
    loading: PropTypes.bool
};

export default AkasiaPosActivityChart;
